import { Injectable, signal, computed, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export interface CartItem {
  productId: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
  size?: string;
  color?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CartService {
  // State
  private cartItems = signal<CartItem[]>([]);

  // UI State
  private _isOpen = signal<boolean>(false);

  // Computed
  totalItems = computed(() => this.cartItems().reduce((acc, item) => acc + item.quantity, 0));
  subtotal = computed(() =>
    this.cartItems().reduce((acc, item) => acc + item.price * item.quantity, 0),
  );

  // Alias for backward compatibility with Header/Drawer
  count = this.totalItems;
  items = this.cartItems.asReadonly();
  isOpen = this._isOpen.asReadonly();

  private platformId = inject(PLATFORM_ID);

  constructor() {
    // Optional: Load from localStorage
    if (isPlatformBrowser(this.platformId)) {
      const saved = localStorage.getItem('seger_cart');
      if (saved) {
        try {
          this.cartItems.set(JSON.parse(saved));
        } catch (e) {
          console.error('Failed to parse cart', e);
        }
      }
    }
  }

  // UI Methods
  toggleCart() {
    this._isOpen.update((v) => !v);
  }

  closeCart() {
    this._isOpen.set(false);
  }

  openCart() {
    this._isOpen.set(true);
  }

  // Cart Methods
  // Cart Methods
  addToCart(item: CartItem) {
    this.cartItems.update((items) => {
      const existing = items.find(
        (i) => i.productId === item.productId && i.size === item.size && i.color === item.color,
      );

      if (existing) {
        return items.map((i) =>
          i.productId === item.productId && i.size === item.size && i.color === item.color
            ? { ...i, quantity: i.quantity + item.quantity }
            : i,
        );
      }
      return [...items, item];
    });
    this.saveCart();
    this.openCart(); // Auto open drawer
  }

  removeFromCart(productId: string, size?: string, color?: string) {
    this.cartItems.update((items) =>
      items.filter((i) => !(i.productId === productId && i.size === size && i.color === color)),
    );
    this.saveCart();
  }

  updateQuantity(productId: string, quantity: number, size?: string, color?: string) {
    this.cartItems.update((items) =>
      items.map((i) =>
        i.productId === productId && i.size === size && i.color === color ? { ...i, quantity } : i,
      ),
    );
    this.saveCart();
  }

  clearCart() {
    this.cartItems.set([]);
    this.saveCart();
  }

  private saveCart() {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('seger_cart', JSON.stringify(this.cartItems()));
    }
  }
}
